import {
  AutocompleteInput,
  Datagrid,
  FunctionField,
  List,
  Pagination,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  TextField,
} from 'react-admin'
import { Link, Stack, Typography } from '@mui/material'

import FilterComponent from '../../components/Filter'

import {
  CHAPTER_DISCUSSION_REPORTS_RESOURCE,
  type DiscussionReportListRecord,
} from './chapterDiscussions.dataProvider'

import type {
  CommentReportReason,
  ListDiscussionReportsQuery,
} from './chapterDiscussions.types'

export interface DiscussionReportListProps {
  initialQuery?: ListDiscussionReportsQuery
}

const REPORT_STATUS_CHOICES = ['open', 'resolved', 'dismissed'].map(status => ({
  id: status,
  name: status,
}))

const REPORT_REASONS: CommentReportReason[] = [
  'policy_violation',
  'harassment',
  'spam_or_irrelevant',
  'personal_information',
  'other',
]

const REPORT_REASON_CHOICES = REPORT_REASONS.map(reason => ({
  id: reason,
  name: reason,
}))

export const reportBookTitleFilterToQuery = (searchText: string) => ({
  title: { $iLike: `%${searchText}%` },
})

export const reportUserNameFilterToQuery = (searchText: string) => ({
  name: { $iLike: `%${searchText}%` },
})

const DiscussionReportPagination = () => (
  <Pagination rowsPerPageOptions={[20, 50, 100]} />
)

const DiscussionReportFilter = (props: Record<string, unknown>) => (
  <FilterComponent styles={{}} {...props}>
    <ReferenceInput
      label="Book (title)"
      source="bookId"
      reference="books"
      alwaysOn>
      <AutocompleteInput
        label="Book (title)"
        optionText="title"
        filterToQuery={reportBookTitleFilterToQuery}
      />
    </ReferenceInput>
    <ReferenceInput
      label="Author"
      source="reportedAuthorId"
      reference="users"
      alwaysOn>
      <AutocompleteInput
        label="Author"
        optionText="name"
        filterToQuery={reportUserNameFilterToQuery}
      />
    </ReferenceInput>
    <ReferenceInput
      label="Reporter"
      source="reporterUserId"
      reference="users"
      alwaysOn>
      <AutocompleteInput
        label="Reporter"
        optionText="name"
        filterToQuery={reportUserNameFilterToQuery}
      />
    </ReferenceInput>
    <SelectInput
      source="status"
      label="Status"
      choices={REPORT_STATUS_CHOICES}
      alwaysOn
    />
    <SelectInput
      source="reason"
      label="Reason"
      choices={REPORT_REASON_CHOICES}
      alwaysOn
    />
  </FilterComponent>
)

export function discussionReportRowClick(
  record: DiscussionReportListRecord,
): string | false {
  if (window.getSelection()?.toString().trim()) return false
  return `/chapter-discussions/${record.commentIdentity}/show`
}

export function DiscussionReportList({
  initialQuery = {},
}: DiscussionReportListProps) {
  const {
    cursor: _cursor,
    limit: _limit,
    mediaType: _mediaType,
    ...filters
  } = initialQuery

  return (
    <List
      resource={CHAPTER_DISCUSSION_REPORTS_RESOURCE}
      title={false}
      actions={false}
      filters={<DiscussionReportFilter />}
      filterDefaultValues={filters}
      pagination={<DiscussionReportPagination />}
      perPage={20}
      sort={{ field: 'createdAt', order: 'DESC' }}
      storeKey="chapter-discussion-reports-list">
      <Datagrid
        bulkActionButtons={false}
        rowClick={(_id, _resource, record) =>
          discussionReportRowClick(record as DiscussionReportListRecord)
        }>
        <FunctionField<DiscussionReportListRecord>
          label="Date"
          source="createdAt"
          sortable={false}
          sx={{ minWidth: 180, whiteSpace: 'nowrap' }}
          render={record => new Date(record.createdAt).toLocaleString()}
        />
        <FunctionField<DiscussionReportListRecord>
          label="Book / chapter"
          source="bookTitle"
          sortable={false}
          render={record => (
            <Stack spacing={0.5}>
              <Typography variant="body2">{record.bookTitle}</Typography>
              <Typography variant="body2" color="text.secondary">
                {record.chapterTitle}
              </Typography>
            </Stack>
          )}
        />
        <FunctionField<DiscussionReportListRecord>
          label="Comment / Rely"
          source="evidence"
          sortable={false}
          sx={{ maxWidth: 420 }}
          render={record => (
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {record.evidence}
            </Typography>
          )}
        />
        <TextField source="reason" label="Reason" sortable={false} />
        <FunctionField<DiscussionReportListRecord>
          label="Note"
          source="note"
          sortable={false}
          sx={{ maxWidth: 320 }}
          render={record => (
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {record.note}
            </Typography>
          )}
        />
        <FunctionField<DiscussionReportListRecord>
          label="Reporter"
          source="reporterName"
          sortable={false}
          render={record => (
            <Link
              href={`#/user-details/${record.reporterId}/show`}
              sx={{ color: '#F47D42' }}
              onClick={event => event.stopPropagation()}>
              <ReferenceField
                source="reporterId"
                reference="users"
                link={false}
                emptyText={record.reporterName}>
                <TextField source="name" />
              </ReferenceField>
            </Link>
          )}
        />
      </Datagrid>
    </List>
  )
}
